import { useCallback, useState } from "react";
import { Button, DatePicker, Input, Typography, message } from "antd";
import dayjs from "dayjs";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import copyToClipboard from "../../utils/copy";
import { useTranslation } from "../../i18n";
import { buildTransferUrl } from "../../utils/fileTransferToken";
import { createTransfer } from "../../utils/fileTransferAdminApi";
import { emptyFormValues, validateForm, formatFileSize } from "../../utils/fileTransferForm";
import "./fileTransfer.css";

const { Title, Paragraph, Text } = Typography;

const PRESET_DAYS = [1, 3, 7, 14, 30];
const PRESET_SIZES = [
  { label: "100 MB", value: 100 * 1024 * 1024 },
  { label: "1 GB", value: 1024 * 1024 * 1024 },
  { label: "10 GB", value: 10 * 1024 * 1024 * 1024 },
  { label: "100 GB", value: 100 * 1024 * 1024 * 1024 },
  { label: "1 TB", value: 1024 * 1024 * 1024 * 1024 },
];

function FileTransferAdmin() {
  const [values, setValues] = useState(emptyFormValues);
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const handleField = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const applyPreset = useCallback((days) => {
    handleField("expiry", dayjs().add(days, "day").hour(23).minute(59).second(59).millisecond(0));
  }, [handleField]);

  const applySize = useCallback((size) => {
    handleField("maxFileSize", size);
  }, [handleField]);

  const disabledDate = useCallback((current) => {
    return current && current < dayjs().startOf("day");
  }, []);

  const handleGenerate = useCallback(async () => {
    setError("");
    setGeneratedUrl("");

    let normalized;
    try {
      normalized = validateForm(values, t);
    } catch (err) {
      setError(err.message);
      return;
    }

    setGenerating(true);
    try {
      const result = await createTransfer(normalized);
      const url = buildTransferUrl(result.id);
      setGeneratedUrl(url);
      message.success(t("fileTransfer.admin.successCreated"));
    } catch (err) {
      setError(err.message || t("fileTransfer.admin.createFailed"));
    } finally {
      setGenerating(false);
    }
  }, [values]);

  const handleCopy = useCallback(async () => {
    if (!generatedUrl) return;
    try {
      await copyToClipboard(generatedUrl);
      message.success(t("fileTransfer.admin.linkCopied"));
    } catch {
      message.error(t("fileTransfer.admin.copyFailed"));
    }
  }, [generatedUrl]);

  return (
    <>
      <PageTitle>
        <Navigation />
      </PageTitle>
      <BackHomeIcon to={PANEL_PATH} />

      <div className="file-transfer-page">
        <div className="custom-h1">
          <span>{t("fileTransfer.admin.title")}</span>
        </div>
        <Paragraph style={{ textAlign: "center", color: "var(--color-text-muted)" }}>
          {t("fileTransfer.admin.subtitle")}
        </Paragraph>

        <div className="file-transfer-admin-section">
          {/* 标题 */}
          <div className="file-transfer-admin-field">
            <label>{t("fileTransfer.admin.fieldTitle")}</label>
            <Input
              placeholder={t("fileTransfer.admin.fieldTitlePlaceholder")}
              value={values.title}
              onChange={(e) => handleField("title", e.target.value)}
            />
            <Paragraph type="secondary" style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}>
              {t("fileTransfer.admin.fieldTitleHint")}
            </Paragraph>
          </div>

          {/* 描述 */}
          <div className="file-transfer-admin-field">
            <label>{t("fileTransfer.admin.fieldDescription")}</label>
            <Input.TextArea
              placeholder={t("fileTransfer.admin.fieldDescriptionPlaceholder")}
              value={values.description}
              onChange={(e) => handleField("description", e.target.value)}
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </div>

          {/* 过期时间 */}
          <div className="file-transfer-admin-field">
            <label>{t("fileTransfer.admin.fieldExpiry")}</label>
            <div className="file-transfer-admin-presets">
              {PRESET_DAYS.map((days) => (
                <Button key={days} onClick={() => applyPreset(days)}>
                  {days === 1 ? t("fileTransfer.admin.day") : t("fileTransfer.admin.days", { days })}
                </Button>
              ))}
            </div>
            <DatePicker
              showTime={{ format: "HH:mm:ss" }}
              format="YYYY-MM-DD HH:mm:ss"
              value={values.expiry}
              onChange={(value) => value && handleField("expiry", value)}
              disabledDate={disabledDate}
              style={{ width: "100%" }}
              inputReadOnly
            />
          </div>

          {/* 单文件大小限制 */}
          <div className="file-transfer-admin-field">
            <label>{t("fileTransfer.admin.fieldMaxFileSize")}</label>
            <div className="file-transfer-admin-presets">
              {PRESET_SIZES.map((opt) => (
                <Button
                  key={opt.value}
                  type={values.maxFileSize === opt.value ? "primary" : "default"}
                  onClick={() => applySize(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              suffix={t("fileTransfer.admin.bytes")}
              value={values.maxFileSize}
              onChange={(e) => handleField("maxFileSize", parseInt(e.target.value, 10) || 0)}
            />
            <Paragraph type="secondary" style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}>
              {t("fileTransfer.admin.maxFileSizeHint", { size: formatFileSize(values.maxFileSize) })}
            </Paragraph>
          </div>

          {/* 文件数量限制 */}
          <div className="file-transfer-admin-field">
            <label>{t("fileTransfer.admin.fieldMaxFiles")}</label>
            <Input
              type="number"
              value={values.maxFiles}
              onChange={(e) => handleField("maxFiles", parseInt(e.target.value, 10) || 1)}
              min={1}
              max={10000}
            />
            <Paragraph type="secondary" style={{ marginTop: "0.5rem", marginBottom: 0, fontSize: "0.85rem" }}>
              {t("fileTransfer.admin.maxFilesHint")}
            </Paragraph>
          </div>

          {error && (
            <Paragraph type="danger" style={{ marginBottom: "1rem" }}>
              {error}
            </Paragraph>
          )}

          <div className="file-transfer-admin-actions">
            <Button type="primary" loading={generating} onClick={handleGenerate}>
              {t("fileTransfer.admin.createButton")}
            </Button>
          </div>

          {generatedUrl && (
            <div className="file-transfer-admin-result">
              <Title level={5}>{t("fileTransfer.admin.resultTitle")}</Title>
              <Paragraph type="secondary">
                {t("fileTransfer.admin.resultDesc")}
              </Paragraph>
              <div className="file-transfer-admin-result__url">
                {generatedUrl}
              </div>
              <div className="file-transfer-admin-actions">
                <Button type="primary" onClick={handleCopy}>
                  {t("fileTransfer.admin.copyLink")}
                </Button>
                <Button onClick={() => window.open(generatedUrl, "_blank")}>
                  {t("fileTransfer.admin.preview")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default FileTransferAdmin;
